import FJVideoUtil from '@src/util/FJVideoUtil';
import { GENERATE_STATE } from '../constants/FJEnumConstant';
import FJDetailManager from '../core/FJDetailManager';
import FJVideoPlayBase from '../core/FJVideoPlayBase.js';
import FJS3Config from '@src/config/FJS3Config';
import FJUtil from '@src/util/FJUtil';
import FJS3NetworkManager from './FJS3NetworkManager';
import FJLongToShortUpdateStore from './FJLongToShortUpdateStore';
import FJCoreConfig from '@src/core/FJCoreConfig';

/** 本会话 createObjectURL 产生的片源地址需在 clear/切换会话时释放，避免 Blob 常驻内存 */
function tryRevokeBlobUrl(url) {
    if (typeof url === 'string' && url.startsWith('blob:')) {
        try {
            URL.revokeObjectURL(url);
        } catch {
            /* 已释放或无效 */
        }
    }
}

/**
 * 导出/解码刚结束时片源尺寸或 seek 可能尚未稳定，短暂重试可提高首帧封面成功率。
 * @param {import('../core/FJDetailManager').default} mgr
 * @param {{ maxAttempts?: number, delayMs?: number }} [opts]
 * @returns {Promise<Blob | null>}
 */
async function getCoverThumbnailBlobWithRetry(mgr, opts = {}) {
    const { maxAttempts = 3, delayMs = 80 } = opts;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        const blob = await mgr.getCoverThumbnailBlob();
        if (blob && blob.size > 0) {
            return blob;
        }
        if (attempt < maxAttempts) {
            await new Promise(r => setTimeout(r, delayMs));
        }
    }
    return null;
}

/**
 * 合并更新某条的 `videoData`。若 `detail` 来自 Redux/Immer 冻结对象，不能直接改 `detail.videoData`；
 * 在会话 `_block.details` 中用新对象替换整条片段（与 `setClipTitle` / `updateDetailData` 一致）。
 * @param {{ id?: string; videoData?: object } | null | undefined} detail
 * @param {Record<string, unknown>} patch
 */
function patchDetailVideoData(detail, patch) {
    if (!detail?.id) return;
    const block = FJProjectStore._block;
    if (!block?.details?.length) return;
    const i = block.details.findIndex(d => d.id === detail.id);
    if (i === -1) return;
    const cur = block.details[i];
    const prev = cur?.videoData && typeof cur.videoData === 'object' ? { ...cur.videoData } : {};
    block.details[i] = { ...cur, videoData: { ...prev, ...patch } };
}

/**
 * 当前打开的「长视频 → 拆条」会话：以选中的 ProjectItem 为核心（_block），
 * 每个成功态 detail 对应一个 FJDetailManager（预览 / 字幕 / 导出入口）。
 * 列表展示仍以 Redux 为准；本地编辑可先改 _block，再通过 thunk 写回 Redux。
 */
const FJProjectStore = {
    /** @type {string} */
    _videoUrl: '',
    /** 来源最近文件 id；非最近入口（如生成页预览）为 null */
    _projectId: null,
    /**
     * 工作副本：与 Redux 中某条 ProjectItem 或仅 details 列表对齐
     * @type {{ videoUrl: string; details: import('../types/FJDetails').FJDetailsListItem[] } | null}
     */
    _block: null,
    /** @type {Map<string, FJDetailManager>} key = detail.id */
    _detailManagers: new Map(),
    /** 当前会话同源片源：**仅一台**解码器，`FJDetailManager` 内 `FJVideoElement` 共享引用（由 `init` 内 `setSource` 唯一一次） */
    _sharedVideoPlayBase: /** @type {import('../core/FJVideoPlayBase.js').default | null} */ (null),
    _videoMeta: null,

    clear() {
        this._detailManagers.forEach(m => {
            try {
                m.clear();
            } catch (e) {
                console.warn('[FJProjectStore] detail manager clear', e);
            }
        });
        this._detailManagers.clear();
        if (this._sharedVideoPlayBase) {
            try {
                this._sharedVideoPlayBase.clearAll();
            } catch (e) {
                console.warn('[FJProjectStore] shared video playback release', e);
            }
            this._sharedVideoPlayBase = null;
        }
        this._block = null;
        tryRevokeBlobUrl(this._videoUrl);
        this._videoUrl = '';
        this._projectId = null;
    },

    /** @returns 当前会话工作副本（`videoUrl` + `details`） */
    getSession() {
        return this._block;
    },

    getVideoUrl() {
        return this._videoUrl;
    },

    getProjectId() {
        return this._projectId;
    },

    /** @returns {string | null | undefined} 当前会话任务 event_id，无则 null */
    getEventId() {
        return this._block?.eventId ?? null;
    },

    /**
     * @param {string} detailId
     * @returns {FJDetailManager | undefined}
     */
    getManager(detailId) {
        return this._detailManagers.get(detailId);
    },

    /**
     * 将 FJDetailManager 包装为 FJVideoPreviewPlayer 所需的 API（与 previewApi 形态一致）
     * @param {FJDetailManager} manager
     */
    createPreviewApi(manager) {
        return {
            setVideoCanvas: c => manager.setVideoCanvas(c),
            setSubtitleView: v => manager.setSubtitleView(v),
            setTimeData: (s, e) => manager.setTimeData(s, e),
            seek: t => manager.seek(t),
            primePlaybackForGesture: () => manager.primePlaybackForGesture(),
            play: (cb, pcb) => manager.play(cb, pcb),
            pause: () => manager.pause(),
            stopLocalPlaybackState: () => manager.stopLocalPlaybackState(),
            drawVideoCanvas: () => manager.drawVideoCanvas(),
            resizeView: () => manager.resizeView(),
            getCurrentTime: () => manager.getCurrentTime(),
            setBufferingListener: fn => manager.setBufferingListener(fn),
        };
    },

    /**
     * 打开详情页时调用：按最近文件项（视频 URL + 片段列表）创建/重建各 FJDetailManager
     * @param {import('../types/FJProject').ProjectItem} item
     */
    async init(item) {
        this.clear();
        this._videoMeta = item.videoMeta;
        // Redux/Immer 中的 item 可能被冻结；会话工作副本需可写（人脸、缩略图等会就地更新）
        this._block = typeof structuredClone === 'function' ? structuredClone(item) : JSON.parse(JSON.stringify(item));
        this._videoUrl = item.videoUrl ?? '';
        const fileData = await FJVideoUtil.getFileListFromLocalStorage(this._videoUrl);
        const cachedSourceBlob = fileData?.data instanceof Blob ? fileData.data : null;
        if (cachedSourceBlob) {
            this._videoUrl = URL.createObjectURL(cachedSourceBlob);
        }
        this._projectId = item.id;
        const exportRatio = this._block.details[0]?.exportRatio ?? '16:9';
        if (exportRatio) {
            const [w, h] = exportRatio.split(':').map(Number);
            const ratio = w / h;
            FJCoreConfig.updateReferSize(ratio);
        }
        const playable = this._block.details.filter(r => r.status !== GENERATE_STATE.FAILED);
        const tasks = [];
        if (playable.length === 0) {
            return;
        }
        const playBase = new FJVideoPlayBase();
        await playBase.setSource(this._videoUrl);
        if (cachedSourceBlob) {
            const sourceInfo = playBase.getSourceInfo();
            playBase.setSourceBlob(cachedSourceBlob, {
                width: sourceInfo?.width ?? 0,
                height: sourceInfo?.height ?? 0,
            });
        }
        this._sharedVideoPlayBase = playBase;

        for (const row of playable) {
            const mgr = new FJDetailManager(this._sharedVideoPlayBase);
            tasks.push(
                mgr
                    .init({
                        videoUrl: this._videoUrl,
                        startTime: row.videoData.startTime,
                        endTime: row.videoData.endTime,
                        subtitles: row.subtitleList,
                        subtitleStyleId: row.subtitleStyleId ?? null,
                        exportRatio: row.exportRatio ?? null,
                        faceData: row.faceData?.list ?? null,
                        id: row.id,
                        language: item.language ?? 'english',
                        isLightVideo: item.videoMeta?.isLightVideo ?? false,
                        stableFragments: item.stableFragments ?? [],
                        fps: item.videoMeta?.fps,
                    })
                    .then(() => {
                        this._detailManagers.set(row.id, mgr);
                    }),
            );
        }
        await Promise.all(tasks);
    },
    async updateDetailData(eventId, detailId, data) {
        const detail = this._block?.details.find(d => d.id === detailId);
        if (!detail) return;
        const { start, end, subtitleList, title, score, summary } = data;
        // 详情预览读 `videoData.startTime/endTime`；仅改顶层 start/end 不会触发播放器时长与进度更新
        const newDetail = {
            ...detail,
            title,
            score,
            summary,
            start,
            end,
            subtitleList,
            ...(detail.videoData
                ? {
                      videoData: {
                          ...detail.videoData,
                          startTime: start,
                          endTime: end,
                      },
                  }
                : {}),
        };
        this._block.details = this._block.details.map(d => (d.id === detailId ? newDetail : d));
        const mgr = this._detailManagers.get(detailId);
        if (mgr) {
            await mgr.updateData({
                startTime: start,
                endTime: end,
                subtitles: subtitleList,
                faceData: data.faceData?.list ?? null,
                language: this._block?.language ?? 'english',
            });
        }
        await this.runPendingManager(eventId, detailId);
    },
    /**
     * 将字幕样式 id 写回 `_block` 中对应片段（跳过失败态；与 FJDetailManager 已应用样式对齐）
     * @param {string} detailId
     * @param {string} subtitleStyleId
     */
    setClipSubtitleId(detailId, subtitleStyleId) {
        if (!this._block) return;
        const i = this._block.details.findIndex(d => d.id === detailId);
        if (i === -1) return;
        const cur = this._block.details[i];
        if (cur.status === GENERATE_STATE.FAILED) return;
        this._block.details[i] = { ...cur, subtitleStyleId };
    },

    /**
     * 在 `_block` 中更新某条片段标题（任意状态；需自行 dispatch 同步 Redux）
     * @param {string} detailId
     * @param {string} title
     */
    setClipTitle(detailId, title) {
        if (!this._block) return;
        const i = this._block.details.findIndex(d => d.id === detailId);
        if (i === -1) return;
        this._block.details[i] = { ...this._block.details[i], title };
    },

    /**
     * 在 `_block` 中更新某条成功态片段的点赞（与 Redux `toggleDetailsItemLiked` 对齐后再 `mergeRecent`）
     * @param {string} detailId
     * @param {boolean} liked
     */
    setClipLiked(detailId, liked) {
        if (!this._block) return;
        const i = this._block.details.findIndex(d => d.id === detailId);
        if (i === -1) return;
        const cur = this._block.details[i];
        if (cur.status !== GENERATE_STATE.SUCCEEDED) return;
        if (!('liked' in cur)) return;
        this._block.details[i] = { ...cur, liked };
    },

    /**
     * 在 `_block` 中更新某条成功态片段的点踩（与 Redux `toggleDetailsItemDisliked` 对齐后再 `mergeRecent`）
     * @param {string} detailId
     * @param {boolean} disliked
     */
    setClipDisliked(detailId, disliked) {
        if (!this._block) return;
        const i = this._block.details.findIndex(d => d.id === detailId);
        if (i === -1) return;
        const cur = this._block.details[i];
        if (cur.status !== GENERATE_STATE.SUCCEEDED) return;
        if (!('disliked' in cur)) return;
        this._block.details[i] = { ...cur, disliked };
    },

    /**
     * 在 `_block` 中更新某条成功态片段的已发布列表（与 Redux `updateDetailsItem` 对齐后再 `mergeRecent`）
     * @param {string} detailId
     * @param {import('../types/FJDetails').FJPublishedItem[]} publishedList
     */
    setClipPublishedList(detailId, publishedList) {
        if (!this._block) return;
        const i = this._block.details.findIndex(d => d.id === detailId);
        if (i === -1) return;
        const cur = this._block.details[i];
        if (cur.status !== GENERATE_STATE.SUCCEEDED) return;
        this._block.details[i] = { ...cur, publishedList };
    },

    /**
     * 从会话中移除一条片段：先释放 `FJDetailManager`（若有），再从 `_block.details` 删除
     * @param {string} detailId
     */
    removeClip(detailId) {
        const mgr = this._detailManagers.get(detailId);
        if (mgr) {
            try {
                mgr.clear();
            } catch (e) {
                console.warn('[FJProjectStore] removeClip clear', e);
            }
            this._detailManagers.delete(detailId);
        }
        if (this._block) {
            this._block.details = this._block.details.filter(d => d.id !== detailId);
            // 若已无片段，整体结束本次会话，释放所有资源
            if (this._block.details.length === 0) {
                this.clear();
            }
        }
    },

    /**
     * 用当前 _block 合并出新的项目列表（纯数据，不 dispatch；由 action 里 dispatch setProjects）
     * @param {import('../types/FJProject').ProjectItem[]} currentItems Redux 当前 projects.items
     * @returns {import('../types/FJProject').ProjectItem[] | null} null 表示无项目会话或未打开 _block
     */
    mergeRecent(currentItems) {
        const fileId = this._projectId;
        if (!fileId || !this._block || !Array.isArray(currentItems)) return null;
        const detailsCopy =
            typeof structuredClone === 'function'
                ? structuredClone(this._block.details)
                : JSON.parse(JSON.stringify(this._block.details));
        return currentItems.map(f =>
            f.id === fileId
                ? {
                      ...f,
                      videoUrl: this._block.videoUrl || f.videoUrl,
                      details: detailsCopy,
                  }
                : f,
        );
    },

    /**
     * 更新指定片段字幕样式（拉取 JSON 后应用到 FJDetailManager）
     * @param {string} detailId
     * @param {{ jsonUrl: string; id?: string }} styleItem 与 FJDetailManager.updateSubtitlesStyle 入参一致
     */
    async applyClipSubtitleStyle(detailId, styleItem) {
        const mgr = this._detailManagers.get(detailId);
        if (!mgr || !styleItem?.jsonUrl || styleItem.id === 'None') return;
        const FJNetworkManager = (await import('../core/FJNetworkManager')).default;
        const FJConfig = (await import('../config/FJConfig')).default;
        const json = await FJNetworkManager.fetchResource(
            `${FJConfig.RESOURCE_SERVER}${styleItem.jsonUrl}?v=${FJConfig.STATIC_RESOURCE_VERSION}`,
        );
        if (!json) return;
        mgr.updateSubtitlesStyle(json);
        mgr.updateView();
        this.setClipSubtitleId(detailId, styleItem.id);
    },
    successDetails() {
        if (!this._block) return;
        this._block.details = this._block.details.map(d => ({ ...d, status: GENERATE_STATE.SUCCEEDED }));
    },

    /**
     * 对当前 `_detailManagers` 内每个 FJDetailManager **依次**做人脸分析（串行，避免并行占满解码槽）。
     * 总进度归一化为 **0～1**；可选 `onOverallProgress` 用于驱动生成页进度条。
     * @param {{ onOverallProgress?: (t: number) => void }} [options]
     * @returns {Promise<void>}
     */
    async runFaceDetectSerial(options = {}) {
        const { onOverallProgress } = options;
        const entries = Array.from(this._detailManagers.entries());
        const n = entries.length;
        const logOverall = t => {
            const v = Math.min(1, Math.max(0, t));
            onOverallProgress?.(v);
        };
        logOverall(0);
        if (n === 0) {
            logOverall(1);
            return;
        }
        let totalSegmentSec = 0;
        let totalDecodeDetectMs = 0;
        let faceClipCount = 0;
        for (let i = 0; i < n; i++) {
            const [detailId, mgr] = entries[i];
            const detail = this._block?.details.find(d => d.id === detailId);
            if (!detail) continue;
            if (detail.faceData?.hasDetected) continue;
            const faceDataBlocks = await mgr.startDetectFace({
                onProgress: p => {
                    const inner = Math.min(1, Math.max(0, (Number(p?.percent) || 0) / 100));
                    logOverall((i + inner) / n);
                },
                onTiming: ({ segmentSec, decodeDetectMs }) => {
                    totalSegmentSec += segmentSec;
                    totalDecodeDetectMs += decodeDetectMs;
                    faceClipCount += 1;
                },
            });
            detail.faceData = {
                list: Array.isArray(faceDataBlocks) ? faceDataBlocks : [],
                hasDetected: true,
            };

            if (FJCoreConfig.isAsyncUpdate) {
                FJLongToShortUpdateStore.uploadSubFaceData(this._block.eventId, detailId, detail.faceData);
            }
            logOverall((i + 1) / n);
        }
        // if (faceClipCount > 0) {
        //     console.info(
        //         `[人脸分析·汇总] 本批检测片段 ${faceClipCount} 条 | 片段时间合计 ${totalSegmentSec.toFixed(
        //             2,
        //         )}s | 解码+检测墙钟合计 ${Math.round(totalDecodeDetectMs) / 1000}s`,
        //     );
        // }
        logOverall(1);
    },

    /**
     * 对已初始化的每个 `FJDetailManager` **依次**生成片段首帧封面图（取帧串行，避免多路解码/合成争抢；**上传**在收尾 `Promise.allSettled` 并行）。
     * 进度语义与 `runFaceDetectSerial` 一致：总进度 **0～1**，按已完成条目数均分（单条内暂不做子进度）。
     *
     * @param {{
     *   onOverallProgress?: (t: number) => void,
     *   onEach?: (detailId: string, blob: Blob | null, index: number) => void,
     *   resolution?: number,
     *   mimeType?: string,
     *   quality?: number,
     * }} [options]
     * @returns {Promise<Map<string, Blob | null>>} detailId → 封面 Blob，失败为 null
     */
    async runCoverThumbnailsSerial(options = {}) {
        const { onOverallProgress } = options;
        const eventId = this._block?.eventId;
        const entries = Array.from(this._detailManagers.entries());
        const n = entries.length;
        const logOverall = t => {
            const v = Math.min(1, Math.max(0, t));
            onOverallProgress?.(v);
        };
        /** @type {Map<string, Blob | null>} */
        logOverall(0);
        if (n === 0) {
            logOverall(1);
            return;
        }
        /** @type {Promise<void>[]} */
        const thumbnailUploads = [];
        for (let i = 0; i < n; i++) {
            const [detailId, mgr] = entries[i];
            const detail = this._block?.details.find(d => d.id === detailId);
            if (!detail) continue;
            if (detail.videoData?.thumbnailUrl) continue;
            patchDetailVideoData(detail, { thumbnailUrlStatus: GENERATE_STATE.PROCESSING });
            try {
                const blob = await getCoverThumbnailBlobWithRetry(mgr);
                thumbnailUploads.push(
                    this.saveThumbnailBlob(eventId, detailId, blob, !FJCoreConfig.isAsyncUpdate).then(thumbnailUrl => {
                        if (thumbnailUrl) {
                            patchDetailVideoData(detail, {
                                thumbnailUrl,
                                thumbnailUrlStatus: GENERATE_STATE.SUCCEEDED,
                            });
                        }
                    }),
                );
            } catch (e) {
                console.warn('[FJProjectStore] runCoverThumbnailsSerial', detailId, e);
            }
            logOverall((i + 1) / n);
        }
        logOverall(1);
        await Promise.allSettled(thumbnailUploads);
    },

    /**
     * 对每个 `FJDetailManager` **依次**执行 `exportSelf`（编码串行，单例编码器同一时间只跑一条导出；**导出文件上传**在收尾 `Promise.allSettled` 并行）。
     * 总进度 **0～1**：与 `runFaceDetectSerial` 相同，按条目索引 `i` 与编码器 `onProgress`（0～1）组合为 `(i + inner) / n`，条结束再 `logOverall((i+1)/n)`。
     *
     * @param {{ onOverallProgress?: (t: number) => void }} [options]
     * @returns {Promise<void>}
     */
    async runExportSelfSerial(options = {}) {
        const { onOverallProgress } = options;
        const entries = Array.from(this._detailManagers.entries());
        const n = entries.length;
        const logOverall = t => {
            const v = Math.min(1, Math.max(0, t));
            onOverallProgress?.(v);
        };
        logOverall(0);
        if (n === 0) {
            logOverall(1);
            return;
        }
        /** @type {Promise<void>[]} */
        const exportUploads = [];
        for (let i = 0; i < n; i++) {
            const [detailId, mgr] = entries[i];
            const detail = this._block?.details.find(d => d.id === detailId);
            if (!detail) continue;
            if (detail.videoData.fileKey) continue;
            try {
                const blob = await mgr.exportSelf({
                    onProgress: p => {
                        const inner = Math.min(1, Math.max(0, Number(p) || 0));
                        logOverall((i + inner) / n);
                    },
                });
                if (blob) {
                    exportUploads.push(
                        this.saveExportedBlob(detailId, blob, !FJCoreConfig.isAsyncUpdate).then(key => {
                            if (key) {
                                patchDetailVideoData(detail, { fileKey: key });
                            }
                        }),
                    );
                }
            } catch (e) {
                console.warn('[FJProjectStore] runExportSelfSerial', detailId, e);
            }
            logOverall((i + 1) / n);
        }
        await Promise.allSettled(exportUploads);
        logOverall(1);
    },

    /**
     * 单条 pending：人脸 → 封面图（读视频）→ 可选导出（编解码占同一 video，与封面串行）→
     * 缩略图与导出文件的上传并行，缩短总墙钟（总耗时优先）。
     */
    async runPendingManager(eventId, detailId) {
        const detail = this._block?.details.find(d => d.id === detailId);
        if (!detail) return null;
        const manager = this._detailManagers.get(detailId);
        if (!manager) return null;
        const disableFaceData = this._block?.disableFaceData ?? false;
        if (!disableFaceData) {
            const faceDataBlocks = await manager.startDetectFace({});
            detail.faceData = {
                list: Array.isArray(faceDataBlocks) ? faceDataBlocks : [],
                hasDetected: true,
            };
            if (FJCoreConfig.isAsyncUpdate) {
                void FJLongToShortUpdateStore.uploadSubFaceData(eventId, detailId, detail.faceData);
            }
        }
        if (eventId !== this.getEventId()) {
            return;
        }

        const ignoreUpload = !FJCoreConfig.isAsyncUpdate;
        patchDetailVideoData(detail, { thumbnailUrlStatus: GENERATE_STATE.PROCESSING, fileKey: '' });
        const thumbBlob = await manager.getCoverThumbnailBlob();

        if (FJCoreConfig.needExport) {
            const exportBlob = await manager.exportSelf();
            const [thumbnailUrl, fileKey] = await Promise.all([
                this.saveThumbnailBlob(eventId, detailId, thumbBlob, ignoreUpload),
                exportBlob ? this.saveExportedBlob(detailId, exportBlob, ignoreUpload) : Promise.resolve(null),
            ]);
            if (thumbnailUrl) {
                patchDetailVideoData(detail, {
                    thumbnailUrl,
                    thumbnailUrlStatus: GENERATE_STATE.SUCCEEDED,
                });
            }
            if (fileKey) {
                patchDetailVideoData(detail, { fileKey });
            }
        } else {
            const thumbnailUrl = await this.saveThumbnailBlob(eventId, detailId, thumbBlob, ignoreUpload);
            if (thumbnailUrl) {
                patchDetailVideoData(detail, {
                    thumbnailUrl,
                    thumbnailUrlStatus: GENERATE_STATE.SUCCEEDED,
                });
            }
        }

        detail.status = GENERATE_STATE.SUCCEEDED;
    },

    async getExportedBlob(detailId) {
        if (!this._block) return null;
        const detail = this._block.details.find(d => d.id === detailId);
        const { fileKey } = detail.videoData;
        if (!fileKey) return null;
        const fileData = await FJVideoUtil.getFileListFromLocalStorage(fileKey);
        if (fileData && fileData.data) {
            return fileData.data;
        } else {
            const url = FJS3Config.S3_SERVER_HOST + FJS3Config.EXPORT_UPLOAD_KEY + fileKey + '.mp4';
            const blob = await FJUtil.getBlobByUrl(url, true);
            if (!blob) {
                return null;
            }
            return blob;
        }
    },
    async saveExportedBlob(detailId, file, ignoreUpload = false) {
        const key = await FJUtil.hashEncode(file);
        try {
            await FJVideoUtil.saveFileListToLocalStorage([{ key, file }]);
            const uploadKey = FJS3Config.EXPORT_UPLOAD_KEY + key + '.mp4';
            FJS3NetworkManager.uploadFileAsync(file, uploadKey);
            const detail = this._block.details.find(d => d.id === detailId);
            if (detail) {
                patchDetailVideoData(detail, { fileKey: key });
            }
            if (this._block.eventId && !ignoreUpload) {
                FJLongToShortUpdateStore.uploadFileKey(this._block.eventId, detailId, key);
            }
            return key;
        } catch (error) {
            console.error('saveExportedBlob error', error);
            return null;
        }
    },
    async saveThumbnailBlob(eventId, detailId, blob, ignoreUpload = false) {
        if (!blob || blob.size === 0) {
            return '';
        }
        try {
            const key = await FJUtil.hashEncode(blob);
            const thumbnailUploadKey = FJS3Config.INPUT_UPLOAD_KEY + key + '.jpg';
            await FJS3NetworkManager.uploadFileAsync(blob, thumbnailUploadKey);
            const thumbnailUrl = FJS3Config.S3_SERVER_HOST + thumbnailUploadKey;
            if (eventId && !ignoreUpload) {
                FJLongToShortUpdateStore.uploadSubThumbnail(eventId, detailId, thumbnailUrl);
            }
            return thumbnailUrl;
        } catch (error) {
            console.error('saveThumbnailBlob error', error);
            return '';
        }
    },
    async getEditAppTimeData(detailId) {
        const mgr = this._detailManagers.get(detailId);
        if (!mgr) return null;
        return await mgr.getEditAppTimeData(this._videoMeta);
    },
};

export default FJProjectStore;
// window.FJProjectStore = FJProjectStore;
